// React provider that supplies the meta models and a function
// to get them by ID.

import { PropsWithChildren, ReactElement, createContext } from "react";

import { arrayMax } from "@nens/shared-utils";

import { MetaModel, useMetaModels } from "../endpoints/meta-model";

export interface MetaModelsContextProps {
  metaModels: MetaModel[];
  getMetaModelById(id: number): MetaModel;
  latestMetaModelId: number;
}

export const MetaModelsContext = createContext<MetaModelsContextProps>({
  metaModels: [],
  getMetaModelById: (id: number) => undefined as unknown as MetaModel,
  latestMetaModelId: -1,
});

interface MetaModelsProviderProps {
  loadingComponent: ReactElement;
}

export function MetaModelsProvider({
  loadingComponent,
  children,
}: PropsWithChildren<MetaModelsProviderProps>) {
  const metaModelsResponse = useMetaModels();

  if (!metaModelsResponse.isSuccess) return loadingComponent;

  const metaModels = metaModelsResponse.data;
  const latestMetaModelIdIdx = arrayMax(metaModels.map((model) => model.id));
  const latestMetaModelId = latestMetaModelIdIdx === -1 ? 0 : metaModels[latestMetaModelIdIdx].id;

  const getMetaModelById = (id: number) => {
    const metaModel = metaModels.find((metaModel) => metaModel.id === id);
    if (metaModel) return metaModel;

    return metaModels[metaModels.length - 1];
  };

  return (
    <MetaModelsContext.Provider value={{ metaModels, getMetaModelById, latestMetaModelId }}>
      {children}
    </MetaModelsContext.Provider>
  );
}
